import { z } from 'zod';
import { ResultStatus } from '../enums.js';
import { AddressSchema } from '../applicant/index.js';
import { ModuleSchema } from '../shared-schemas.js';

// Step 1: Add school/university (institution) schema
export const HighestStudyInstitutionSchema = z.object({
  country: z.string().min(1, "Country can't be empty"),
  institutionName: z.string().min(1, "The name can't be empty"),
  institutionAddress: AddressSchema.omit({ country: true }).extend({
    state: z.string().optional(), // Override to make state optional
  }),
});

export type IHighestStudyInstitution = z.infer<typeof HighestStudyInstitutionSchema>;

// Step 2: Add study schema - qualification type selection
// Represents a user's selection of a qualification type and its associated grading system
export const HighestStudyQualificationTypeSelectionSchema = z
  .object({
    id: z.string().optional(), // unique identifier for this studyRow (to distinguish multiple rows with same qualificationType)
    qualificationType: z.string().min(1, 'Please select the qualification type'), // id
    qualificationTypeLabel: z.string().optional(),
    customQualificationTypeName: z.string().optional(), // required if 'Other' is selected
    gradingSystem: z.string().min(1, 'Please select the grading system'),
    gradingSystemLabel: z.string().optional(),
    groupId: z.string().optional(), // groupId to track which selections belong to the same study group
    resultStatus: z.nativeEnum(ResultStatus).optional(), // Per-group result status
    taughtInEnglish: z.boolean().optional(), // Per-group taught in English
  })
  .refine(
    data => {
      // If qualificationTypeLabel is 'Other', customQualificationTypeName must be filled
      if (data.qualificationTypeLabel === 'Other') {
        const customName = data.customQualificationTypeName;
        return customName && typeof customName === 'string' && customName.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Please enter the qualification type',
      path: ['customQualificationTypeName'],
    },
  );

export const HighestStudyAddStudySchema = z.object({
  country: z.string().min(1),
  qualificationTypeSelections: z.array(HighestStudyQualificationTypeSelectionSchema).min(1),
  resultStatus: z.nativeEnum(ResultStatus),
  taughtInEnglish: z.boolean().nullish(),
});

/**
 * Enhancer for HighestStudyAddStudySchema to support view-dependent constraints.
 * - Requires customQualificationTypeName when qualificationType === 'Other' (handled in selection schema)
 * - Requires gradingSystem when the corresponding selection exposes one or more grading options
 * - Requires taughtInEnglish answer when isUK is false
 */
export function enhanceHighestStudyAddStudySchema(
  _gradingOptionsPerSelection: Record<number, Array<{ label: string; value: string }>>,
  isUK: boolean,
): z.ZodType<z.infer<typeof HighestStudyAddStudySchema>> {
  // Promote taughtInEnglish to a field-level requirement when country is non-UK,
  // so the error is attached directly to the field on first submit (even if other fields fail).
  const SchemaWithCountryRule = HighestStudyAddStudySchema.extend({
    taughtInEnglish: isUK
      ? z.boolean().nullish()
      : z.union([z.boolean(), z.null(), z.undefined()]).refine(val => typeof val === 'boolean', {
          message: 'Please select Yes or No',
        }),
  });

  return SchemaWithCountryRule;
}

/**
 * Step 3: Add qualification schema (frontend-focused, only fields shown in the modal)
 *
 * Simplified qualification schema for Highest Study Step 3.
 * Excludes fields collected in Step 2 (qualification type, grading system).
 * Only used in Your Information > Your Highest Study flow.
 *
 * For other qualification forms (Education History, Offers), use QualificationSchema instead.
 *
 * Note: All fields are optional by default. The enhancer function makes them required based on:
 * - Visibility flags from qualification type (subjectVisible, awardingOrganisationsVisible, modulesVisible)
 * - Pending status (isPending)
 */
export const HighestStudyAddQualificationSchema = z.object({
  subject: z.string().optional(),
  grade: z.string().optional(),
  graduationDate: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val) return true; // Allow empty when optional
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
        const MIN_YEAR = currentYear - 130;
        const [year, month] = val.split('-');
        const graduationDateYear = year && year.length === 4 ? parseInt(year, 10) : undefined;
        const graduationDateMonth = month && month.length > 0 ? parseInt(month, 10) : undefined;

        // Check if year is valid and within range
        if (!graduationDateYear || graduationDateYear <= MIN_YEAR) return false;

        // Prevent future dates: year must be <= current year
        if (graduationDateYear > currentYear) return false;

        // If year equals current year, month must be <= current month
        if (graduationDateYear === currentYear && graduationDateMonth && graduationDateMonth > currentMonth) {
          return false;
        }

        return true;
      },
      { message: 'Invalid date' },
    ),
  awardingOrganisationId: z.string().optional(),
  modules: z.array(ModuleSchema).optional(),
});

export type IHighestStudyAddQualification = z.infer<typeof HighestStudyAddQualificationSchema>;

export function enhanceHighestStudyAddQualificationSchema(
  date?: string,
  _country?: string,
  awardingOrgRequired?: boolean,
  isPending?: boolean,
  subjectVisible?: boolean,
  resultStatus?: ResultStatus,
): z.ZodEffects<z.AnyZodObject> {
  // Promote fields to field-level requirements based on visibility flags and pending status,
  // so errors are attached directly to the field on first submit (even if other fields fail).
  // This follows the same pattern as taughtInEnglish in HighestStudyAddStudySchema.
  const SchemaWithConditionalRules = HighestStudyAddQualificationSchema.extend({
    subject:
      subjectVisible === false
        ? z.string().optional()
        : z.union([z.string(), z.undefined()]).refine(
            val => {
              if (!val || val.trim().length === 0) return false; // Required when visible
              return val.length <= 100;
            },
            val => {
              if (!val || val.trim().length === 0) return { message: "Subject can't be empty" };
              return { message: 'Subject is too long.' };
            },
          ),
    grade: isPending
      ? z.string().optional()
      : z.union([z.string(), z.undefined()]).refine(
          val => {
            if (!val || val.trim().length === 0) return false; // Required when not pending
            return val.length <= 100;
          },
          val => {
            if (!val || val.trim().length === 0) return { message: "Grade can't be empty" };
            return { message: 'Grade is too long.' };
          },
        ),
    graduationDate: isPending
      ? z.string().optional()
      : z.union([z.string(), z.undefined()]).refine(
          val => {
            if (!val) return false; // Required when not pending
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
            const MIN_YEAR = currentYear - 130;
            const [year, month] = val.split('-');
            const graduationDateYear = year && year.length === 4 ? parseInt(year, 10) : undefined;
            const graduationDateMonth = month && month.length > 0 ? parseInt(month, 10) : undefined;

            // Check if year is valid and within range
            if (!graduationDateYear || graduationDateYear <= MIN_YEAR) return false;

            // InterimResults: allow current month + future dates, no past dates
            if (resultStatus === ResultStatus.InterimResults) {
              // Prevent past dates: year must be >= current year
              if (graduationDateYear < currentYear) return false;

              // If year equals current year, month must be >= current month
              if (graduationDateYear === currentYear && graduationDateMonth && graduationDateMonth < currentMonth) {
                return false;
              }

              return true;
            }

            // FinalResults (or default): allow past dates + current month, no future dates
            // Prevent future dates: year must be <= current year
            if (graduationDateYear > currentYear) return false;

            // If year equals current year, month must be <= current month
            if (graduationDateYear === currentYear && graduationDateMonth && graduationDateMonth > currentMonth) {
              return false;
            }

            return true;
          },
          { message: 'Invalid date' },
        ),
    awardingOrganisationId:
      isPending || !awardingOrgRequired
        ? z.string().optional()
        : z.string().min(1, 'Please select an awarding organisation'),
  });

  return SchemaWithConditionalRules.superRefine((data, ctx) => {
    if (isPending) return; // Skip cross-field validation when pending
    if (!date || !data.graduationDate) return; // Skip cross-field validation when study start date is not available
    const startDate = new Date(date);
    const targetDate = new Date(data.graduationDate as string);
    const isDateValid = targetDate >= startDate;
    if (!isDateValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Qualification date must be after your study start date',
        path: ['graduationDate'],
      });
    }
  });
}
